
export interface ApiEnvelope<T = unknown> {
  code: number;
  message: string;
  data: T | null;
  error_code?: string;
}

export interface ListMeta {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface PaginatedList<T> {
  data: T[];
  meta: ListMeta;
}

interface ApiListPayload<T = unknown> {
  items: T[];
  pagination?: ListMeta;
}

export function isApiEnvelope(body: unknown): body is ApiEnvelope {
  if (body == null || typeof body !== 'object') return false;
  const o = body as Record<string, unknown>;
  return typeof o.code === 'number' && typeof o.message === 'string' && 'data' in o;
}

export function unwrapApiResponse(body: unknown): unknown {
  if (body === '' || body === null || body === undefined) {
    return null;
  }

  if (!isApiEnvelope(body)) return body;

  const { data, message } = body;

  if (data === null || data === undefined) {
    return null;
  }

  if (typeof data === 'object' && data !== null && 'items' in data) {
    const list = data as ApiListPayload;
    const record = data as Record<string, unknown>;
    const keys = Object.keys(record);
    const isListEnvelope =
      Array.isArray(list.items) &&
      (('pagination' in record && keys.every((k) => k === 'items' || k === 'pagination')) ||
        keys.every((k) => k === 'items'));
    if (isListEnvelope) {
      if (list.pagination) {
        return { data: list.items, meta: list.pagination } satisfies PaginatedList<unknown>;
      }
      return list.items;
    }
  }

  return data;
}

export function unwrapList<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value != null && typeof value === 'object' && 'data' in value) {
    const data = (value as PaginatedList<T>).data;
    return Array.isArray(data) ? data : [];
  }
  return [];
}

export function isPaginatedList<T>(value: unknown): value is PaginatedList<T> {
  return (
    value != null &&
    typeof value === 'object' &&
    'data' in value &&
    'meta' in value &&
    Array.isArray((value as PaginatedList<T>).data)
  );
}
